/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type SyntheticEvent, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';
import ErrorIcon from '@atlaskit/icon/core/status-error';
import Lozenge from '@atlaskit/lozenge';
import { Inline, Stack, Text } from '@atlaskit/primitives/compiled';
import { Radio } from '@atlaskit/radio';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import {
	type ActionKeys,
	JIRA_ISSUE_MOVE_KEY,
} from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type Sprint } from '@atlassian/conversation-assistant-message-actions-api/jira';
import {
	type ActionRendererFor,
	type ActionRendererKeyToPayload,
	type ActionWith,
} from '@atlassian/conversation-assistant-message-actions-api/types';
import type { MessageAction } from '@atlassian/conversation-assistant-service-api/types/message-actions';

import { ActionCardFooter, ActionCardWrapper } from '../../common/ui/action-card-wrapper';
import { IssueSelector } from '../../common/ui/issue-selector';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

const styles = cssMap({
	errorMessage: {
		paddingInlineStart: token('space.025'),
	},
});

const JIRA_ISSUE_MOVE_INTERACTION_SOURCE = 'jiraIssueMoveRenderer';

export const filterJiraIssueMoveActions = (
	actions: MessageAction[],
): ActionWith<ActionRendererKeyToPayload[typeof ActionKeys.JiraIssueMove]>[] => {
	const filteredActions = actions.filter(assertIsJiraIssueMoveAction);
	return filteredActions as ActionWith<
		ActionRendererKeyToPayload[typeof ActionKeys.JiraIssueMove]
	>[];
};

const assertIsJiraIssueMoveAction = (action: MessageAction): MessageAction => {
	if (action.key !== JIRA_ISSUE_MOVE_KEY) {
		throw new Error('Invalid action key for JiraIssueMoveAction');
	}
	return action;
};

export const JiraIssueMoveAction = (
	props: ActionRendererFor<typeof ActionKeys.JiraIssueMove>,
): JSX.Element => {
	return <JiraIssueMoveActionNewLayout {...props} />;
};

export const JiraIssueMoveActionNewLayout = ({
	actions,
	getStatus,
	getReadonly,
	recipientAgentId,
	recipientAgentNamedId,
	conversationId,
	messageId,
	onCustomDataConfirm,
	sendMessageConfirmedTools,
}: ActionRendererFor<typeof ActionKeys.JiraIssueMove>): JSX.Element | null => {
	const { formatMessage } = useIntl();
	const [selectedIssues, setSelectedIssues] = useState<string[]>([]);
	const [selectedSprint, setSelectedSprint] = useState<string>('');
	const [shouldShowErrorMessage, setShouldShowErrorMessage] = useState(false);

	const { sendAnalyticsEvent } = useAnalytics();

	const firstAction = actions[0];
	const {
		data: { issues, sprints },
	} = firstAction;
	const readonly = getReadonly(firstAction);
	const status = getStatus(firstAction);

	const onChangeSprint = useCallback(
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		({ currentTarget: { value } }: SyntheticEvent<any>) => {
			setSelectedSprint(value);
			if (shouldShowErrorMessage) {
				setShouldShowErrorMessage(selectedIssues.length === 0 || value === '');
			}
		},
		[selectedIssues.length, shouldShowErrorMessage],
	);

	const handleSelectIssue = useCallback(
		(issueKeys: string[]) => {
			setSelectedIssues(issueKeys);
			if (shouldShowErrorMessage) {
				setShouldShowErrorMessage(issueKeys.length === 0 || selectedSprint === '');
			}
		},
		[selectedSprint, shouldShowErrorMessage],
	);

	const getSprintAnalytics = useCallback(
		(sprints: Sprint[]): [Sprint | undefined, number | undefined] => {
			let sprintIndex;
			const sprint = sprints.find(({ sprintId }, index) => {
				sprintIndex = index;
				return sprintId === selectedSprint;
			});
			return [sprint, sprintIndex];
		},
		[selectedSprint],
	);

	const moveConfirm = () => {
		const canMove = selectedIssues.length > 0 && selectedSprint !== '';
		if (canMove) {
			const [sprint, sprintIndex] = getSprintAnalytics(sprints);
			const moveData = {
				issues: issues.filter(({ issueKey }) => selectedIssues.includes(issueKey)),
				sprintId: selectedSprint,
			};
			sendAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'moveActionButton',
				attributes: {
					recipientAgentNamedId,
					actionKey: JIRA_ISSUE_MOVE_KEY,
					selectedIssueCount: selectedIssues.length,
					totalIssueCount: issues.length,
					sprintState: sprint?.sprintState,
					sprintIndex,
				},
			});
			if (
				FeatureGates.getExperimentValue('rovo_tools_resumption_migration_exp', 'isEnabled', false)
			) {
				sendMessageConfirmedTools({
					agentId: recipientAgentId,
					conversationId,
					messageSources: {
						interactionSource: JIRA_ISSUE_MOVE_INTERACTION_SOURCE,
					},
					confirmed_tools: {
						message_id: messageId,
						tools: [buildToolFromAction(firstAction, 'confirmed', moveData)],
					},
				});
			} else {
				onCustomDataConfirm(firstAction, moveData);
			}
		}
		setShouldShowErrorMessage(!canMove);
	};

	if (issues.length <= 0 || sprints.length <= 0) {
		return null;
	}

	return (
		<Stack space="space.250">
			<ActionCardWrapper
				title={formatMessage(messages.jiraIssueMoveActionCardTitle)}
				product="jira"
			>
				<Stack space="space.150">
					<Text>{formatMessage(messages.jiraIssueMoveListMessageText)}</Text>
					<IssueSelector
						initialIssues={issues}
						title={formatMessage(messages.jiraIssueMoveListTitleText)}
						isInitiallyExpanded
						onIssuesSelected={handleSelectIssue}
						readonly={readonly}
					/>
					<Text>{formatMessage(messages.jiraIssueMoveSprintSelectorText)}</Text>
					{sprints.map(({ sprintId, sprintName, sprintState }) => (
						<Inline key={sprintId} space="space.100" alignInline="start" alignBlock="center">
							<Radio
								isChecked={sprintId === selectedSprint}
								isDisabled={readonly}
								onChange={onChangeSprint}
								name={sprintName}
								label={sprintName}
								value={sprintId}
							></Radio>
							<Lozenge appearance={sprintState === 'ACTIVE' ? 'information' : 'neutral'}>
								{sprintState}
							</Lozenge>
						</Inline>
					))}
				</Stack>
				<ActionCardFooter>
					<Button
						appearance="primary"
						isDisabled={readonly || status !== 'idle'}
						onClick={moveConfirm}
					>
						{formatMessage(messages.jiraIssueMoveButtonLabelText)}
					</Button>
				</ActionCardFooter>
			</ActionCardWrapper>
			<div role="region" aria-live="polite">
				{shouldShowErrorMessage && (
					<Inline alignBlock="center" space="space.075" xcss={styles.errorMessage}>
						<ErrorIcon label="" color={token('color.icon.danger')} size="small" />
						<Text color="color.text.danger" size="small">
							{formatMessage(messages.jiraIssueMoveButtonErrorMessageText)}
						</Text>
					</Inline>
				)}
			</div>
		</Stack>
	);
};
